import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

/**
 * useVersionCheck monitors public/version.json to detect new deployments.
 * When a new build timestamp is detected, it alerts the user and provides
 * a seamless way to reload the app without manual cache clearing.
 */
export default function useVersionCheck() {
  const currentVersionRef = useRef(null);
  const isPromptingRef = useRef(false);

  useEffect(() => {
    // Only enable automated version checking in production/staging environments
    const isLocal =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1';

    if (isLocal) return;

    const checkLatestVersion = async () => {
      try {
        const response = await fetch(`/version.json?t=${Date.now()}`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
          },
        });

        if (!response.ok) return;

        const data = await response.json();
        const latestVersion = data?.version;

        if (!latestVersion) return;

        if (currentVersionRef.current === null) {
          // Record version loaded at startup
          currentVersionRef.current = latestVersion;
        } else if (currentVersionRef.current !== latestVersion && !isPromptingRef.current) {
          isPromptingRef.current = true;

          // Notify user of new version
          toast.info(
            <div>
              <p style={{ margin: 0, fontWeight: 600 }}>A new update is available!</p>
              <p style={{ margin: '4px 0 8px 0', fontSize: '13px' }}>
                Please refresh to get the latest features and bug fixes.
              </p>
              <button
                type="button"
                onClick={() => {
                  window.location.reload();
                }}
                style={{
                  background: '#06634E',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '5px 12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Update Now
              </button>
            </div>,
            {
              toastId: 'app-version-update',
              autoClose: false,
              closeOnClick: false,
            }
          );
        }
      } catch (error) {
        // Silently catch network errors (e.g. temporary offline)
      }
    };

    // 1. Initial check
    checkLatestVersion();

    // 2. Periodic polling every 5 minutes
    const intervalId = setInterval(checkLatestVersion, 5 * 60 * 1000);

    // 3. Re-check whenever user switches back to this tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkLatestVersion();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', checkLatestVersion);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', checkLatestVersion);
    };
  }, []);
}
